import React, { useEffect, useMemo, useState } from 'react';
import LeafletMap from '../../components/LeafletMap';
import {
	Button,
	DownloadButton,
	Input
} from '../../components/StyledComponents';
import { StatusLabel } from '../../components/ui/StatusLabel';
import DateFormat from '../../lib.r2/utils/DateFormat';
import Store from '../../store/Store';
import { Body } from '../../components/layout/Body';
import { useParams } from 'react-router-dom';
import { Page } from '../../components/ui/Page';
import { IconCloseOrange, DangerIcon, TimerIcon } from '../../components/Icons';
import OrderUploadSection from '../../components/features/OrderUploadSection';
import FlightDetailsSection from '../../components/features/FlightDetailsSection';
import { useDialog } from '../../hooks/useDialog';

export interface OrderPageProviderProps {
	id?: string;
}

interface SelectedOrder {
	orderId: string;
	name: string;
	orderState: string;
	mission?: string;
	submitted?: string;
	dateFrom: string;
	dateTo: string;
	coordinates: Record<string, unknown>;
	purposes?: Array<{ name: string; resultTypes: Array<{ name: string; id?: number }> }>;
	orderResultTypes?: Array<{
		resultType?: {
			missionName?: string;
			purposeName?: string;
			name?: string;
			id?: number;
		};
	}>;
	flightHubTaskUuid?: string | null;
	flightHubTaskName?: string | null;
	flightHubTaskBeginAt?: string | null;
	flightHubStatus?: string | null;
	flightHubMediaStatus?: string | null;
	flightHubMediaZipStatus?: string | null;
	flightHubMediaZipBlobName?: string | null;
	pix4dProjectId?: number | null;
	pix4dDetailUrl?: string | null;
	pix4dStatus?: string | null;
	pix4dResultsStatus?: string | null;
	droneModelName?: string | null;
	droneSerialNumber?: string | null;
	dockModelName?: string | null;
	dockSerialNumber?: string | null;
	gsdCm?: number | null;
	coordinateSystem?: string | null;
	geoidModel?: string | null;
	calibratedImages?: string | null;
	imageCount?: number | null;
	sensorModel?: string | null;
}

interface OrderUpload {
	purpose: string;
	resultType: string;
	fileName?: string;
	downloadUrl?: string;
	[key: string]: unknown;
}

interface MapSetup {
	exportZip?: () => void;
}

export default function OrderPageProvider({ id }: OrderPageProviderProps) {
	const { showConfirmDialog, showMessageDialog } = useDialog();
	const params = useParams();
	const orderId = params.orderId ?? id;

	const [action, setAction] = useState('');
	const [selectedOrder, orderUploads] = Store.use(
		'selectedOrder',
		'orderUploads'
	) as unknown as [
			SelectedOrder | undefined,
			OrderUpload[] | null | undefined
		];

	const [orderState, setOrderState] = useState(selectedOrder?.orderState ?? '');
	const [flightTaskName, setFlightTaskName] = useState('');
	const [flightTaskBeginAt, setFlightTaskBeginAt] = useState('');
	const [sendingFlightTask, setSendingFlightTask] = useState(false);
	const [savingFlightTaskMedia, setSavingFlightTaskMedia] = useState(false);
	const [downloadingFlightTaskMediaZip, setDownloadingFlightTaskMediaZip] = useState(false);
	const [startingPix4dFromFlightTaskMedia, setStartingPix4dFromFlightTaskMedia] = useState(false);
	const [retrievingPix4dResults, setRetrievingPix4dResults] = useState(false);
	const [rerunningPix4d, setRerunningPix4d] = useState(false);

	const mapSetup = useMemo<MapSetup>(() => ({}), []);

	useEffect(() => {
		const finalOrderId = orderId ?? selectedOrder?.orderId;

		if (!finalOrderId) return;

		Store.getUploads(finalOrderId).catch((err) => {
			console.error('getUploads error:', err);
		});
	}, [orderId, selectedOrder?.orderId]);

	useEffect(() => {
		if (selectedOrder?.orderState != null) setOrderState(selectedOrder.orderState);
	}, [selectedOrder?.orderState]);

	useEffect(() => {
		if (orderId && selectedOrder?.orderId !== orderId) {
			Store.selectOrderById(orderId);
		}
	}, [orderId, selectedOrder?.orderId]);

	useEffect(() => {
		const finalOrderId = orderId ?? selectedOrder?.orderId;
		if (!finalOrderId) return;
		Store.refreshSelectedOrder(finalOrderId).catch((err: unknown) => {
			console.error('refreshSelectedOrder error:', err);
		});
	}, [orderId, selectedOrder?.orderId]);

	useEffect(() => {
		if (!selectedOrder?.orderId) return;

		const isRunning = (s?: string | null) => !!s && (s === 'QUEUED' || s.startsWith('RUNNING'));
		const hasActiveBackgroundWork =
			isRunning(selectedOrder.flightHubMediaStatus) ||
			isRunning(selectedOrder.flightHubMediaZipStatus) ||
			selectedOrder.pix4dStatus === 'QUEUED' ||
			selectedOrder.pix4dStatus === 'STARTING' ||
			selectedOrder.pix4dStatus === 'PROCESSING' ||
			selectedOrder.pix4dResultsStatus === 'QUEUED' ||
			selectedOrder.pix4dResultsStatus === 'RUNNING' ||
			selectedOrder.pix4dResultsStatus === 'WAITING';

		if (!hasActiveBackgroundWork) return;

		const orderIdToPoll = selectedOrder.orderId;
		const refresh = () => {
			Store.refreshSelectedOrder(orderIdToPoll).catch((err: unknown) => {
				console.error('refreshSelectedOrder error:', err);
			});
		};

		const interval = window.setInterval(refresh, 30000);
		return () => window.clearInterval(interval);
	}, [
		selectedOrder?.orderId,
		selectedOrder?.flightHubMediaStatus,
		selectedOrder?.flightHubMediaZipStatus,
		selectedOrder?.pix4dStatus,
		selectedOrder?.pix4dResultsStatus,
	]);

	useEffect(() => {
		if (!selectedOrder?.orderId) return;
		if (selectedOrder.pix4dResultsStatus !== 'DONE') return;
		Store.getUploads(selectedOrder.orderId).catch((err: unknown) => {
			console.error('getUploads error:', err);
		});
	}, [selectedOrder?.orderId, selectedOrder?.pix4dResultsStatus]);

	if (!selectedOrder)
		return (
			<Page>
				<Body title="no Order selected!" />
			</Page>
		);

	const updateOrderState = async (
		newState: string,
		newAction?: string,
		showActionPage = true
	) => {
		await Store.changeOrderState(selectedOrder.orderId, newState);
		Store.setPopupMessage('ok', 'Auftrag erfolgreich!', '');
		(selectedOrder as { orderState: string }).orderState = newState;
		setOrderState(newState);
		if (showActionPage) {
			setAction(newAction ?? newState);
		}
	};

	const onCancelOrder = async () => {
		showConfirmDialog({
			type: 'warning',
			title: 'Auftrag stornieren?',
			text: 'Sind Sie sicher, dass Sie den Auftrag stornieren wollen?',
			icon: <DangerIcon />,
			confirmLabel: 'Ja, stornieren',
			onConfirm: async () => {
				await updateOrderState('CANCELED');
				showMessageDialog({
					title: 'Der Auftrag wurde storniert!',
					text: 'Sie haben die Bestellung storniert.',
				});
			},
		});
	};

	const onDelayedOrder = async () => {
		showConfirmDialog({
			type: 'warning',
			title: 'Auftrag verzögern?',
			text: 'Sind Sie sicher, dass Sie den Auftrag verzögern wollen?',
			icon: <DangerIcon />,
			confirmLabel: 'Ja, verzögern',
			onConfirm: async () => {
				await updateOrderState('DELAYED');
				showMessageDialog({
					title: 'Der Auftrag wurde verzögert!',
					text: 'Sie haben die Bestellung verzögert.',
				});
			},
		});
	};

	const onCompleteOrder = async () => {
		showConfirmDialog({
			type: 'warning',
			title: 'Auftrag abschließen?',
			text: 'Sind Sie sicher, dass Sie den Auftrag abschließen wollen?',
			icon: <DangerIcon />,
			confirmLabel: 'Ja, abschließen',
			onConfirm: async () => {
				await updateOrderState('COMPLETED', undefined, false);
				showMessageDialog({
					title: 'Der Auftrag wurde abgeschlossen!',
					text: 'Sie haben den Auftrag abgeschlossen.',
				});
			},
		});
	};

	const normalizedPurposes =
		selectedOrder.purposes ??
		Object.values(
			(selectedOrder.orderResultTypes ?? []).reduce<
				Record<string, { name: string; resultTypes: Array<{ name: string; id?: number }> }>
			>((acc, item) => {
				const purposeName = item.resultType?.purposeName;
				const resultTypeName = item.resultType?.name;
				const resultTypeId = item.resultType?.id;

				if (!purposeName || !resultTypeName) return acc;

				if (!acc[purposeName]) {
					acc[purposeName] = { name: purposeName, resultTypes: [] };
				}

				acc[purposeName].resultTypes.push({ name: resultTypeName, id: resultTypeId });

				return acc;
			}, {})
		);

	const purposesMap: Record<string, string[]> = {};

	normalizedPurposes.forEach((e) => {
		purposesMap[e.name] = e.resultTypes.map((rt) => rt.name);
	});

	const mission =
		selectedOrder.mission ??
		selectedOrder.orderResultTypes?.find((item) => item.resultType?.missionName)
			?.resultType?.missionName ??
		'';

	const orderAction = Store.getOrderAction(action) as
		| { title: string; text: string }
		| undefined;

	if (orderAction)
		return (
			<Page center>
				<Body center>
					<h1>{orderAction.title}</h1>
					<h3>{orderAction.text}</h3>
					<Button primary onClick={() => setAction('')}>
						Fertig
					</Button>
				</Body>
			</Page>
		);

	const canSendFlightTask =
		orderState.includes('ONGOING') &&
		flightTaskName.trim().length > 0 &&
		flightTaskBeginAt &&
		new Date(flightTaskBeginAt).getTime() > Date.now();

	const sendFlightTask = async () => {
		if (!selectedOrder || !canSendFlightTask || sendingFlightTask) return;

		setSendingFlightTask(true);

		try {
			const url =
				Store.config.api.url +
				'provider/order/flight-task?orderId=' +
				selectedOrder.orderId;

			const body = JSON.stringify({
				name: flightTaskName.trim(),
				beginAt: flightTaskBeginAt,
			});

			const response = await fetch(url, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'x-csrf-token': Store.token ?? '',
				},
				body,
			});

			if (response.ok) {
				Store.setPopupMessage('ok', 'Flugauftrag erfolgreich an FlightHub gesendet.', '');
				setFlightTaskName('');
				setFlightTaskBeginAt('');
			} else {
				Store.setPopupMessage('error', 'Fehler beim Senden des Flugauftrags.', '');
			}
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim Senden des Flugauftrags.', '');
		} finally {
			setSendingFlightTask(false);
		}
	};

	const canSaveFlightTaskMedia =
		orderState.includes('ONGOING') &&
		!!selectedOrder?.orderId &&
		!!selectedOrder?.flightHubTaskUuid;

	const isFlightHubMediaRunning =
		selectedOrder.flightHubMediaStatus === 'QUEUED' ||
		!!selectedOrder.flightHubMediaStatus?.startsWith('RUNNING');
	const isFlightHubZipRunning =
		selectedOrder.flightHubMediaZipStatus === 'QUEUED' ||
		!!selectedOrder.flightHubMediaZipStatus?.startsWith('RUNNING');
	const isPix4dRunning =
		selectedOrder.pix4dStatus === 'QUEUED' ||
		selectedOrder.pix4dStatus === 'STARTING' ||
		selectedOrder.pix4dStatus === 'PROCESSING';
	const isPix4dResultsRunning =
		selectedOrder.pix4dResultsStatus === 'QUEUED' ||
		selectedOrder.pix4dResultsStatus === 'RUNNING' ||
		selectedOrder.pix4dResultsStatus === 'WAITING';

	const flightHubStatusLower = (selectedOrder.flightHubStatus ?? '').toLowerCase();
	const flightInProgress =
		!flightHubStatusLower ||
		flightHubStatusLower === 'waiting' ||
		flightHubStatusLower === 'executing' ||
		flightHubStatusLower === 'paused';
	const flightFinished = !!selectedOrder.flightHubStatus && !flightInProgress;
	const mediaDone = selectedOrder.flightHubMediaStatus === 'DONE';
	const pix4dDone = selectedOrder.pix4dStatus === 'DONE';

	const showSaveMediaButton = flightFinished || !!selectedOrder.flightHubMediaStatus;
	const showZipButton = mediaDone || !!selectedOrder.flightHubMediaZipStatus;
	const showPix4dStartButton = mediaDone || !!selectedOrder.pix4dStatus;
	const showPix4dResultsButton =
		pix4dDone || !!selectedOrder.pix4dProjectId || !!selectedOrder.pix4dResultsStatus;

	const showAnyActionButton =
		showSaveMediaButton || showZipButton || showPix4dStartButton || showPix4dResultsButton;

	const saveFlightTaskMedia = async () => {
		if (!selectedOrder || savingFlightTaskMedia) return;
		setSavingFlightTaskMedia(true);
		try {
			const response = await fetch(
				Store.config.api.url + 'provider/flighthub/save-flight-task-media',
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'x-csrf-token': Store.token ?? '',
					},
					body: JSON.stringify({ orderId: selectedOrder.orderId }),
				}
			);

			if (response.ok) {
				await Store.refreshSelectedOrder(selectedOrder.orderId);
				Store.setPopupMessage(
					'ok',
					'FlightHub Medien gestartet.',
					'Die Verarbeitung läuft jetzt im Hintergrund.'
				);
			} else {
				Store.setPopupMessage('error', 'Fehler beim Speichern der FlightHub Medien.', '');
			}
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim Speichern der FlightHub Medien.', '');
		} finally {
			setSavingFlightTaskMedia(false);
		}
	};

	const downloadFlightTaskMediaZip = async () => {
		if (!selectedOrder || downloadingFlightTaskMediaZip) return;
		setDownloadingFlightTaskMediaZip(true);
		try {
			if (selectedOrder.flightHubMediaZipStatus === 'DONE') {
				const url =
					Store.config.api.url +
					'provider/flighthub/flight-task-media-zip?orderId=' +
					encodeURIComponent(selectedOrder.orderId);

				const response = await fetch(url, {
					method: 'GET',
					credentials: 'include',
					headers: { 'x-csrf-token': Store.token ?? '' },
				});

				if (!response.ok) {
					Store.setPopupMessage('error', 'Fehler beim Download der Medien (ZIP).', '');
					return;
				}

				const blob = await response.blob();
				const objectUrl = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = objectUrl;
				a.download = `flight-task-media-${selectedOrder.orderId}.zip`;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(objectUrl);
			} else {
				const response = await fetch(
					Store.config.api.url + 'provider/flighthub/flight-task-media-zip',
					{
						method: 'POST',
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json',
							'x-csrf-token': Store.token ?? '',
						},
						body: JSON.stringify({ orderId: selectedOrder.orderId }),
					}
				);

				if (!response.ok) {
					Store.setPopupMessage('error', 'Fehler beim Vorbereiten der Medien (ZIP).', '');
					return;
				}

				await Store.refreshSelectedOrder(selectedOrder.orderId);
				Store.setPopupMessage(
					'ok',
					'ZIP-Erstellung gestartet.',
					'Das ZIP wird im Hintergrund vorbereitet und kann danach heruntergeladen werden.'
				);
			}
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim Download der Medien (ZIP).', '');
		} finally {
			setDownloadingFlightTaskMediaZip(false);
		}
	};

	const startPix4dWithFlightTaskMedia = async () => {
		if (!selectedOrder || startingPix4dFromFlightTaskMedia) return;
		setStartingPix4dFromFlightTaskMedia(true);
		try {
			const response = await fetch(Store.config.api.url + 'provider/flighthub/pix4d', {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'x-csrf-token': Store.token ?? '',
				},
				body: JSON.stringify({ orderId: selectedOrder.orderId }),
			});

			if (!response.ok) {
				Store.setPopupMessage('error', 'Fehler beim Starten von Pix4D.', '');
				return;
			}

			await Store.refreshSelectedOrder(selectedOrder.orderId);
			Store.setPopupMessage(
				'ok',
				'Pix4D Verarbeitung gestartet.',
				'Die Verarbeitung läuft jetzt im Hintergrund.'
			);
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim Starten von Pix4D.', '');
		} finally {
			setStartingPix4dFromFlightTaskMedia(false);
		}
	};

	const retrievePix4dResults = async () => {
		if (!selectedOrder || retrievingPix4dResults || !selectedOrder.pix4dProjectId) return;
		setRetrievingPix4dResults(true);
		try {
			const response = await fetch(
				Store.config.api.url + 'provider/flighthub/pix4d/results',
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'x-csrf-token': Store.token ?? '',
					},
					body: JSON.stringify({ orderId: selectedOrder.orderId }),
				}
			);

			if (!response.ok) {
				Store.setPopupMessage('error', 'Fehler beim Abrufen der Pix4D Dateien.', '');
				return;
			}

			await Store.refreshSelectedOrder(selectedOrder.orderId);
			Store.setPopupMessage(
				'ok',
				'Pix4D Abruf gestartet.',
				'Die Modell-Dateien werden jetzt im Hintergrund synchronisiert.'
			);
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim Abrufen der Pix4D Dateien.', '');
		} finally {
			setRetrievingPix4dResults(false);
		}
	};

	const rerunPix4d = async () => {
		if (!selectedOrder || rerunningPix4d || !selectedOrder.pix4dProjectId) return;
		setRerunningPix4d(true);
		try {
			const response = await fetch(
				Store.config.api.url + 'provider/flighthub/pix4d/rerun',
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'x-csrf-token': Store.token ?? '',
					},
					body: JSON.stringify({ orderId: selectedOrder.orderId }),
				}
			);

			if (!response.ok) {
				Store.setPopupMessage('error', 'Fehler beim erneuten Starten von Pix4D.', '');
				return;
			}

			await Store.refreshSelectedOrder(selectedOrder.orderId);
			Store.setPopupMessage(
				'ok',
				'Pix4D Neuberechnung gestartet.',
				'Das bestehende Modell wird ohne erneuten Upload neu verarbeitet.'
			);
		} catch (e) {
			Store.setPopupMessage('error', 'Fehler beim erneuten Starten von Pix4D.', '');
		} finally {
			setRerunningPix4d(false);
		}
	};

	const PIX4D_RESULTS_BUNDLE = 'pix4d-results.zip';
	const seenPix4dFiles = new Set<string>();
	const pix4dDownloadableFiles = (orderUploads ?? []).filter((upload) => {
		const fileName = String(upload?.fileName ?? '');
		if (!upload?.downloadUrl || !fileName || seenPix4dFiles.has(fileName)) return false;
		if (fileName.toLowerCase() !== PIX4D_RESULTS_BUNDLE) return false;
		seenPix4dFiles.add(fileName);
		return true;
	});

	return (
		<div className="max-w-[1300px] m-auto p-[25px] md:px-[20px] md:py-[20px] mt-7">
			<div className="order-page flex gap-5 justify-between">
				<div className="order-page-left">
					<div className="border-b border-[#333] pb-8 mb-8">
						<div className="flex items-center gap-3 flex-wrap justify-between mb-5">
							<div className="flex h-full items-center">
								<h1
									className="text-2xl font-bold max-w-[350px] break-words whitespace-normal"
									style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
								>
									{selectedOrder.name}
								</h1>
							</div>
							<StatusLabel>
								{orderState as 'CANCELED' | 'COMPLETED' | 'DELAYED' | 'AWAITING_PAYMENT' | 'AWAITING_APPROVAL' | 'ONGOING'}
							</StatusLabel>
						</div>

						<span className="text-[#666666]">
							Erstellt am: {selectedOrder.submitted ? DateFormat(selectedOrder.submitted) : ''}
						</span>
						<br />
						<span className="text-[#666666]">{selectedOrder.orderId}</span>
					</div>

					<h2 className="text-2xl mb-4 font-normal">Buchungsdetails</h2>

					<div className="mb-4">
						<h3 className="text-base font-semibold p-0 m-0">Anwendungsfall</h3>
						<div className="text-base">{mission}</div>
					</div>

					<div className="mb-4">
						<h3 className="text-base font-semibold p-0 m-0">Dateiformate</h3>
						<div>
							{Object.entries(purposesMap).map(([purpose, types]) => (
								<div className="text-base" key={purpose}>
									{purpose}: {types.join(', ')}
								</div>
							))}
						</div>
					</div>

					<div className="mb-4">
						<h3 className="text-base font-semibold p-0 m-0">
							Gewünschter Befliegungszeitraum
						</h3>
						<div className="text-base">
							{DateFormat(selectedOrder.dateFrom, 'dd.mm.yyyy') +
								' - ' +
								DateFormat(selectedOrder.dateTo, 'dd.mm.yyyy')}
						</div>
					</div>

					<FlightDetailsSection order={selectedOrder} />

					<div className="my-8 pt-6 border-t border-[#333]">
						<div>
							<h2 className="text-2xl mb-4 font-normal">Downloads</h2>
						</div>

						<div className="flex flex-col gap-3 max-w-[300px]">
							<DownloadButton
								onClick={() => mapSetup.exportZip?.()}
								label="Daten für den Flug"
								self
							/>
							{orderState.includes('COMPLETED') && (
								<DownloadButton
									// TODO: Connect this button to the new backend download flow.
									onClick={() => {}}
									label="Auswertung abrufen"
									self
								/>
							)}
						</div>
					</div>

					{orderState.includes('ONGOING') && (
						<>
							<div className="mt-8 mb-4 pb-6 pt-8 border-t border-[#333]">
								<h2 className="text-2xl mb-4 font-normal">Flugauftrag (FlightHub)</h2>

								{selectedOrder.flightHubTaskUuid ? (
									<div className="flex flex-col gap-4 max-w-[450px]">
										<div>
											<h3 className="text-base font-semibold p-0 m-0">Status</h3>
											<div className="text-base">
												{selectedOrder.flightHubStatus && selectedOrder.flightHubStatus.trim() !== ''
													? selectedOrder.flightHubStatus
													: 'Geplant'}
											</div>
										</div>

										<div>
											<h3 className="text-base font-semibold p-0 m-0">Geplant für</h3>
											<div className="text-base">
												{selectedOrder.flightHubTaskBeginAt
													? DateFormat(selectedOrder.flightHubTaskBeginAt, 'dd.mm.yyyy HH:MM')
													: '-'}
											</div>
										</div>

										<div>
											<h3 className="text-base font-semibold p-0 m-0">Name</h3>
											<div className="text-base">{selectedOrder.flightHubTaskName ?? '-'}</div>
										</div>

										{showAnyActionButton && (
											<div className="flex flex-col gap-3 mt-2">
												{showSaveMediaButton && (
													<Button
														primary
														enabled={canSaveFlightTaskMedia && !savingFlightTaskMedia && !isFlightHubMediaRunning && !mediaDone}
														onClick={saveFlightTaskMedia}
													>
														{savingFlightTaskMedia || isFlightHubMediaRunning
															? 'FlightHub Medien laufen im Hintergrund...'
															: mediaDone
																? 'FlightHub Medien gespeichert'
																: 'FlightHub Medien speichern'}
													</Button>
												)}

												{showZipButton && (
													<Button
														primary
														enabled={canSaveFlightTaskMedia && !downloadingFlightTaskMediaZip && !isFlightHubZipRunning}
														onClick={downloadFlightTaskMediaZip}
													>
														{downloadingFlightTaskMediaZip || isFlightHubZipRunning
															? 'ZIP wird vorbereitet...'
															: selectedOrder.flightHubMediaZipStatus === 'DONE'
																? 'FlightHub Medien (ZIP) downloaden'
																: 'FlightHub Medien (ZIP) vorbereiten'}
													</Button>
												)}

												{showPix4dStartButton && (
													<Button
														primary
														enabled={canSaveFlightTaskMedia && !startingPix4dFromFlightTaskMedia && !isPix4dRunning && !pix4dDone}
														onClick={startPix4dWithFlightTaskMedia}
													>
														{startingPix4dFromFlightTaskMedia || isPix4dRunning
															? 'Pix4D läuft im Hintergrund...'
															: pix4dDone
																? 'An Pix4D gesendet'
																: 'FlightHub Medien an Pix4D senden'}
													</Button>
												)}

												{showPix4dResultsButton && (
													<Button
														primary
														enabled={!!selectedOrder.pix4dProjectId && !retrievingPix4dResults && !isPix4dResultsRunning}
														onClick={retrievePix4dResults}
													>
														{retrievingPix4dResults || isPix4dResultsRunning
															? 'Pix4D Dateien werden synchronisiert...'
															: 'Pix4D Modelle abrufen'}
													</Button>
												)}

												{!!selectedOrder.pix4dProjectId && (
													<Button
														primary
														enabled={!rerunningPix4d && !isPix4dRunning && !isPix4dResultsRunning}
														onClick={rerunPix4d}
													>
														{rerunningPix4d || isPix4dRunning
															? 'Pix4D läuft im Hintergrund...'
															: 'Pix4D erneut berechnen (Rerun)'}
													</Button>
												)}
											</div>
										)}

										{(selectedOrder.flightHubMediaStatus || selectedOrder.flightHubMediaZipStatus) && (
											<div className="text-sm text-[#666666]">
												{selectedOrder.flightHubMediaStatus ? `Medien: ${selectedOrder.flightHubMediaStatus}` : ''}
												{selectedOrder.flightHubMediaZipStatus ? ` | ZIP: ${selectedOrder.flightHubMediaZipStatus}` : ''}
											</div>
										)}

										{(selectedOrder.pix4dStatus || selectedOrder.pix4dResultsStatus) && (
											<div className="text-sm text-[#666666]">
												{selectedOrder.pix4dStatus ? `Pix4D Status: ${selectedOrder.pix4dStatus}` : ''}
												{selectedOrder.pix4dResultsStatus ? ` | Dateien: ${selectedOrder.pix4dResultsStatus}` : ''}
												{selectedOrder.pix4dDetailUrl ? ` | ${selectedOrder.pix4dDetailUrl}` : ''}
											</div>
										)}

										{pix4dDownloadableFiles.length > 0 && (
											<div className="flex flex-col gap-2 mt-2">
												<h3 className="text-base font-semibold p-0 m-0">Pix4D Dateien</h3>
												{pix4dDownloadableFiles.map((file, index) => (
													<DownloadButton
														key={`${file.fileName ?? 'pix4d-file'}-${index}`}
														label={String(file.fileName ?? 'Pix4D Datei herunterladen')}
														url={String(file.downloadUrl)}
														self
													/>
												))}
											</div>
										)}
									</div>
								) : (
									<div className="flex flex-col gap-4 my- max-w-[450px]">
										<div>
											<Input
												id="flightTaskName"
												data-pw="input-flightTaskName"
												label="Flugauftrag Name *"
												value={flightTaskName}
												onChange={setFlightTaskName}
											/>
										</div>

										<div>
											<Input
												id="flightTaskBeginAt"
												data-pw="input-flightTaskBeginAt"
												label="Der Flugauftrag beginnt bei *"
												type="datetime-local"
												value={flightTaskBeginAt}
												onChange={setFlightTaskBeginAt}
											/>
										</div>

										<Button
											className="max-w-[250px] mt-3"
											primary
											enabled={!!canSendFlightTask && !sendingFlightTask}
											onClick={sendFlightTask}
										>
											Flugauftrag an FlightHub senden
										</Button>
									</div>
								)}
							</div>

							<div className="mt-6 mb-3 pt-6 border-t border-[#333]">
								<h2 className="text-2xl mb-4 font-normal">Upload</h2>

								<div className="mt-6 text-[#666666]">
									Um abzuschließen, folgende Dateien hochladen:
								</div>

								<OrderUploadSection
									orderId={selectedOrder.orderId}
									orderUploads={orderUploads}
									normalizedPurposes={normalizedPurposes}
								/>
							</div>
						</>
					)}

					{orderState === 'AWAITING_APPROVAL' && (
						<div className="flex flex-row gap-4">
							<Button
								className="border border-[#E1320F] text-[#E1320F] max-w-[180px] gap-2"
								data-pw="button-cancel-order"
								onClick={onDelayedOrder}
							>
								<TimerIcon /> Auftrag verzögern
							</Button>

							<Button
								className="border border-[#E1320F] text-[#E1320F] max-w-[180px] gap-2"
								data-pw="button-cancel-order"
								onClick={onCancelOrder}
							>
								<IconCloseOrange /> Auftrag stornieren
							</Button>

							<Button primary onClick={() => updateOrderState('ONGOING')}>
								Auftrag freigeben
							</Button>
						</div>
					)}

					{orderState === 'DELAYED' && (
						<div className="flex flex-row gap-4">
							<Button
								className="border border-[#E1320F] text-[#E1320F] max-w-[180px] gap-2"
								data-pw="button-cancel-order"
								onClick={onCancelOrder}
							>
								<IconCloseOrange /> Auftrag stornieren
							</Button>

							<Button
								className="max-w-[180px]"
								primary
								onClick={() => updateOrderState('ONGOING')}
							>
								Auftrag freigeben
							</Button>
						</div>
					)}

					{orderState.includes('ONGOING') && (
						<div className="flex flex-row gap-4">
							<Button
								className="border border-[#E1320F] text-[#E1320F] max-w-[180px] gap-2"
								data-pw="button-cancel-order"
								onClick={onDelayedOrder}
							>
								<TimerIcon /> Auftrag verzögern
							</Button>

							<Button
								className="max-w-[180px]"
								primary
								onClick={onCompleteOrder}
							>
								Auftrag abschließen
							</Button>
						</div>
					)}

					{(orderState.includes('AWAITING') ||
						orderState.includes('ONGOING') ||
						orderState.includes('DELAYED') ||
						action === 'ACCEPT') && (
							<>
								{action === 'ACCEPT' && (
									<div className="flex flex-row lg:flex-col gap-4">
										<Button data-pw="button-order-cancel" onClick={() => setAction('')}>
											Abbrechen
										</Button>

										<Button
											primary
											data-pw="button-order-submit"
											onClick={() => updateOrderState('ONGOING', 'ACCEPTED')}
										>
											Einreichen
										</Button>
									</div>
								)}
							</>
						)}

					{!orderState.includes('COMPLETED') &&
						!orderState.includes('CANCELED') &&
						!orderState.includes('DELAYED') && (
							<div className="mt-6 text-[#666666]">
								Sie können Aufträge bei wetterbedingten oder anderen auftretenden Problemen verzögern.
							</div>
						)}
				</div>

				<div className="order-page-right max-w-[600px]">
					<div style={{ aspectRatio: '1 / 1', width: '100%', maxWidth: 600, minWidth: 300 }}>
						<LeafletMap
							data-pw="leaflet-map-order-page"
							coordinates={selectedOrder.coordinates}
							setup={mapSetup as unknown as Record<string, unknown>}
							view3d={Store.config.view3d}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
